import React, { useEffect, useMemo, useState } from 'react';
import {
  X,
  Code2,
  Copy,
  Check,
  KeyRound,
  Shield,
  ExternalLink,
  AlertCircle,
} from 'lucide-react';
import type { Theme, ThemeColors, ReportDefinition } from './types';
import { getReportAnalyticsHubViewUrl } from './utils';

const THEME_CONFIG: Record<Theme, ThemeColors> = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: { primary: 'text-white', secondary: 'text-neutral-300', muted: 'text-neutral-400' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-neutral-700',
    input: 'bg-neutral-800 border-neutral-600 text-white placeholder-neutral-500',
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-100',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: { primary: 'text-gray-900', secondary: 'text-gray-700', muted: 'text-gray-500' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-gray-100',
    input: 'bg-white border-gray-300 text-gray-900 placeholder-gray-400',
  },
};

interface ReportEmbedModalProps {
  theme?: Theme;
  report: ReportDefinition;
  onClose: () => void;
}

interface CopyFieldProps {
  label: string;
  value: string;
  colors: ThemeColors;
  copyValue?: string;
  multiline?: boolean;
  hideLabel?: boolean;
}

function CopyField({ label, value, colors, copyValue, multiline = false, hideLabel = false }: CopyFieldProps) {
  const [copied, setCopied] = useState(false);
  const textToCopy = copyValue ?? value;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <div>
      <div className={`flex items-center mb-1.5 ${hideLabel ? 'justify-end' : 'justify-between'}`}>
        <label className={`text-[11px] font-medium ${colors.text.secondary} ${hideLabel ? 'sr-only' : ''}`}>
          {label}
        </label>
        <button
          type="button"
          onClick={handleCopy}
          className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${colors.hover} ${
            copied ? 'text-emerald-400' : colors.text.muted
          }`}
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      {multiline ? (
        <pre
          className={`px-3 py-2.5 rounded-lg border text-[10px] leading-relaxed overflow-x-auto ${colors.border} ${colors.secondary} ${colors.text.primary} font-mono`}
        >
          {value}
        </pre>
      ) : (
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${colors.border} ${colors.secondary}`}
        >
          <code className={`flex-1 text-[11px] truncate font-mono ${colors.text.primary}`}>{value}</code>
        </div>
      )}
    </div>
  );
}

const ReportEmbedModal: React.FC<ReportEmbedModalProps> = ({ theme = 'dark', report, onClose }) => {
  const colors = THEME_CONFIG[theme];
  const reportDbId = report.id;
  const authToken = localStorage.getItem('token') ?? '';
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const embedUrl = useMemo(() => getReportAnalyticsHubViewUrl(report), [report]);

  const iframeSnippet = useMemo(
    () =>
      `<iframe\n  src="${embedUrl}"\n  width="100%"\n  height="640"\n  frameborder="0"\n  allow="same-origin"\n  title="${report.name}"\n></iframe>`,
    [embedUrl, report.name]
  );

  const maskedToken =
    authToken.length > 12
      ? `${authToken.slice(0, 8)}${'•'.repeat(Math.min(authToken.length - 12, 24))}${authToken.slice(-4)}`
      : authToken || '—';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`w-full max-w-5xl rounded-xl border shadow-2xl ${colors.border} ${colors.primary} flex flex-col max-h-[92vh]`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="embed-modal-title"
      >
        <div className={`flex items-center justify-between px-5 py-4 border-b ${colors.border} flex-shrink-0`}>
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <Code2 className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <h2 id="embed-modal-title" className={`text-sm font-semibold ${colors.text.primary} truncate`}>
                Embed Analytics
              </h2>
              <p className={`text-[10px] ${colors.text.muted} truncate`}>{report.name}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className={`p-1.5 rounded-md ${colors.hover} ${colors.text.muted}`}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4 min-h-0">
          {!authToken && (
            <div className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10`}>
              <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className={`text-[10px] leading-relaxed text-amber-200/90`}>
                No auth token found in local storage. Sign in again to embed with authentication.
              </p>
            </div>
          )}

          <div className={`rounded-lg border overflow-hidden ${colors.border} bg-white min-h-[360px]`}>
            <iframe
              src={embedUrl}
              title={`${report.name} embed preview`}
              className="w-full h-[min(52vh,520px)] border-0"
              allow="same-origin"
            />
          </div>

          <div className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border ${colors.border} bg-indigo-500/10 border-indigo-500/20`}>
            <Shield className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
            <p className={`text-[10px] leading-relaxed ${colors.text.muted}`}>
              Embed URL uses your current session token from local storage. Anyone with this URL can
              access the report while the token is valid.
            </p>
          </div>

          <CopyField label="Embed URL" value={embedUrl} colors={colors} />

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className={`text-[11px] font-medium ${colors.text.secondary} flex items-center gap-1.5`}>
                <KeyRound className="w-3.5 h-3.5 text-amber-400" />
                Session token
              </span>
              <button
                type="button"
                onClick={() => setShowToken((v) => !v)}
                className={`px-2 py-0.5 rounded text-[10px] font-medium ${colors.hover} ${colors.text.muted}`}
              >
                {showToken ? 'Hide' : 'Reveal'}
              </button>
            </div>
            <CopyField
              label="Session token"
              hideLabel
              value={showToken ? authToken : maskedToken}
              copyValue={authToken}
              colors={colors}
            />
          </div>

          <CopyField
            label="iframe snippet"
            value={iframeSnippet}
            colors={colors}
            multiline
          />

          <div className={`grid grid-cols-2 gap-2 pt-1`}>
            <div className={`px-3 py-2 rounded-lg border ${colors.border} ${colors.secondary}`}>
              <p className={`text-[10px] ${colors.text.muted}`}>Report ID</p>
              <p className={`text-[11px] font-mono truncate ${colors.text.primary}`}>{reportDbId}</p>
            </div>
            <div className={`px-3 py-2 rounded-lg border ${colors.border} ${colors.secondary}`}>
              <p className={`text-[10px] ${colors.text.muted}`}>Auth scheme</p>
              <p className={`text-[11px] ${colors.text.primary}`}>Query token</p>
            </div>
          </div>
        </div>

        <div className={`flex items-center justify-between px-5 py-3 border-t ${colors.border} ${colors.secondary} flex-shrink-0`}>
          <a
            href={embedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-1 text-[10px] font-medium text-indigo-400 hover:text-indigo-300`}
          >
            <ExternalLink className="w-3 h-3" />
            Open in new tab
          </a>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-medium"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportEmbedModal;
